import { Outlet, useOutletContext, useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import { ProfileOverviewDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import type { RouteContext } from 'modules/employees/lib/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { MyTrainingsDatatable } from 'modules/trainings/components/MyTrainings/MyTrainingsDatatable'
import { MyTrainingDatatableProvider } from 'modules/trainings/components/MyTrainings/MyTrainingsDatatable/DatatableSource'
import { NextSessionRow } from 'modules/trainings/components/MyTrainings/NextSessionRow'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'
import coursesBackground from 'modules/upselling_kit/lib/assets/blankSlate/locked-courses-bg.svg'

const EmployeeTrainingList = () => {
  const { id } = useParams()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const { employee } = useOutletContext<RouteContext>()
  const { company, canRequestUpsell } = useCurrentSession()

  const { data, loading } = useQuery(ProfileOverviewDocument, {
    variables: {
      companyId: company.id,
      employeeId: employee.id,
      moduleIds: [AvailableModules.trainings],
    },
  })
  const moduleAvailabilities = data?.companies.moduleAvailabilitiesConnection.nodes
  const trainingsAvailable =
    moduleAvailabilities &&
    isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.trainings)

  if (loading || (!trainingsAvailable && !canRequestUpsell)) return null

  return (
    <>
      {trainingsAvailable ? (
        <ResponsivePage.Body>
          <MyTrainingDatatableProvider
            employeeId={employee.attributes.id}
            accessId={employee.attributes.access_id}
            rowNavigation={(trainingId) =>
              navigation.employees.id.training.detail.view.fullPath({
                id: String(id),
                trainingId: trainingId,
              })
            }
            debouncedSearch={debouncedSearch}
          >
            <Box gap="s48">
              <NextSessionRow
                accessId={employee.attributes.access_id}
                sessionUrl={(sessionId) =>
                  navigation.employees.id.training.nextSession.fullPath({
                    id: String(id),
                    sessionId: sessionId,
                  })
                }
              />
              <Box gap="s32">
                <Text.Header2>{i18n.t('trainings.my_training.all_courses')}</Text.Header2>
                <MyTrainingsDatatable onSearch={onSearch} search={search} />
              </Box>
              <Outlet context={{ employee }} />
            </Box>
          </MyTrainingDatatableProvider>
        </ResponsivePage.Body>
      ) : (
        <UpsellingKit.EmptyState
          moduleRequested={AvailableModules.trainings}
          requestSource="Courses tab in employee profile"
          requestReason={ValidReasons.MissingAddon}
          background={coursesBackground}
          title={i18n.t('upselling_kit.empty_state.title')}
          description={i18n.t('upselling_kit.empty_state.description')}
        />
      )}
    </>
  )
}

export default EmployeeTrainingList
